// Auto-generated UserPermission enum usage hook
import { useMemo, ReactNode } from "react";
import { useSelector } from "react-redux";
import {
  UserPermission,
  isValidUserPermission,
} from "../types/generated/UserPermission";
import { RootState } from "../types/RootState";
import { store } from "../redux/store";

// Your preferred computed map approach - Fixed to use permissionsMap (traditional approach)
export const usePermissions = () => {
  const userPermissionsMap = useSelector(
    (state: RootState) => state.user.permissionsMap || {}
  );

  return useMemo(() => {
    const permissionMap: Record<string, boolean> = {};

    // Convert enum values to uppercase format used in permissionsMap
    Object.values(UserPermission).forEach((key) => {
      // Convert from 'integrated_help_manage' to 'INTEGRATED_HELP_MANAGE'
      const uppercaseKey = key.toUpperCase();
      permissionMap[key] = Boolean(userPermissionsMap[uppercaseKey]);
    });

    return permissionMap;
  }, [userPermissionsMap]);
};

// Helper function that uses the hook properly
export const useHasPermission = (permission: UserPermission): boolean => {
  const permissions = usePermissions();
  return permissions[permission] || false;
};

// Non-hook version that can be called anywhere (including inside callbacks)
// Gets permissions from Redux store directly, or from passed loginInfo - Fixed to use permissionsMap
export const hasPermission = (
  permission: UserPermission,
  loginInfo?: { permissionsMap?: Record<string, boolean> } | null
): boolean => {
  try {
    // Convert from 'integrated_help_manage' to 'INTEGRATED_HELP_MANAGE'
    const uppercaseKey = permission.toUpperCase();
    // If loginInfo is provided, use it directly
    if (loginInfo !== undefined) {
      const userPermissionsMap = loginInfo?.permissionsMap || {};
      return Boolean(userPermissionsMap[uppercaseKey]);
    }
    // Otherwise, get from Redux store
    const state = store.getState();
    const userPermissionsMap = state.user?.permissionsMap || {};
    return Boolean(userPermissionsMap[uppercaseKey]);
  } catch {
    return false;
  }
};

// API response validation - Updated to handle permissionsMap format
export const parsePermissions = (
  apiPermissions: unknown
): Record<string, boolean> => {
  if (!apiPermissions || typeof apiPermissions !== "object") return {};

  const permissions: Record<string, boolean> = {};
  const permissionsObj = apiPermissions as Record<string, unknown>;

  // Handle both formats: uppercase keys from permissionsMap and lowercase keys from permissions
  Object.keys(permissionsObj).forEach((key) => {
    // Try lowercase version first (enum format)
    const lowercaseKey = key.toLowerCase();
    if (isValidUserPermission(lowercaseKey)) {
      permissions[lowercaseKey] = Boolean(permissionsObj[key]);
    }
    // Also try direct key match
    else if (isValidUserPermission(key)) {
      permissions[key] = Boolean(permissionsObj[key]);
    }
  });

  return permissions;
};

// Example component usage
export const PermissionGuard = ({
  permission,
  children,
}: {
  permission: UserPermission;
  children: ReactNode;
}) => {
  const hasPermission = useHasPermission(permission);

  // Your pattern: permissions["permission_key"] => hasPermission(UserPermission.PERMISSION_KEY)
  return hasPermission ? <>{children}</> : null;
};

// Helper for multiple permissions (AND logic)
export const useHasAllPermissions = (
  permissions: UserPermission[]
): boolean => {
  const permissionMap = usePermissions();
  return permissions.every((permission) => permissionMap[permission]);
};

// Helper for multiple permissions (OR logic)
export const useHasAnyPermission = (permissions: UserPermission[]): boolean => {
  const permissionMap = usePermissions();
  return permissions.some((permission) => permissionMap[permission]);
};

// Non-hook versions for multiple permissions
export const hasAllPermissions = (permissions: UserPermission[]): boolean => {
  return permissions.every((permission) => hasPermission(permission));
};

export const hasAnyPermission = (permissions: UserPermission[]): boolean => {
  return permissions.some((permission) => hasPermission(permission));
};
